import { Recipes, Recipe } from "@/types/recipes";
import NativeClient from "@/client/native-client";
import { CommonRecipe, CommonRecipeFoodId } from "./commonRecipe";

export interface RootState {
  version: string;
}

export interface HomeState {
  recommendRecipes: Recipes | null;
  rankingRecipes: Recipes | null;
  bannerPosition: number;
  banners: Banners | null;
  services: InformationServices | null;
  recentMyList: FavoriteRecipeClips | null;
  myList: FavoriteRecipeClips | null;
  // 食品提案
  foodRecommendationList: FoodRecommendationList | null;
  // 一食献立提案API
  oneMenuRecommendation: OneMenuRecommendation | null;
}

/** HOMEレシピ種別 */
export const HOME_RECIPE_TYPE = {
  /** 人気レシピ */
  RECOMMEND: "home_recommend",
  /** おすすめ食材 */
  RECOMMEND_FOOD: "home_recommend_food",
  /** サービス一覧 */
  SERVICE: "home_service",
  /** マイリスト */
  MYLIST: "home_mylist"
} as const;

/** 献立種別 */
export const DISH_TYPE = {
  /** 主菜 */
  MAIN: "main",
  /** 副菜 */
  SUB: "sub"
} as const;

export const DISH_POSITION = {
  MAIN_DISH: 0,
  SUB1_DISH: 1,
  SUB2_DISH: 2
} as const;

export const HOME_BANNER_TYPE = {
  WEB_CONTENT: "WEB_CONTENT",
  RECIPE_SEARCH: "RECIPE_SEARCH",
  MY_APPLIANCES: "MY_APPLIANCES",
  PANTRY: "PANTRY",
  MY_LIST: "MY_LIST"
} as const;

export interface Option {
  q: string | null;
  order: string | null;
  modelName: Array<string> | null;
  eoj: string;
}

export interface Banner {
  imageUrl: string | null;
  contentsUrl: string | null;
  externalContentsFlg: boolean;
}

export interface Banners {
  banners: Array<Banner>;
  lastUpdated: string;
}

export interface InformationSeviceImage {
  top: string | null;
  thumb: string | null;
}

export interface InformationService {
  title: string | null;
  description: string | null;
  image: InformationSeviceImage | null;
  contentsUrl: string | null;
  externalContentsFlg: boolean;
}

export interface InformationServices {
  services: Array<InformationService>;
  lastUpdated: string;
}

export class BannerData {
  id: number;
  imageUrl: string | null;
  contentsUrl: string | null;
  externalContentsFlg: boolean;
  constructor(
    id: number,
    imageUrl: string | null,
    contentsUrl: string | null,
    externalContentsFlg: boolean
  ) {
    this.id = id;
    this.imageUrl = imageUrl;
    this.contentsUrl = contentsUrl;
    this.externalContentsFlg = externalContentsFlg;
  }
}

export class ServiceImageData {
  top: string | null;
  thumb: string | null;
  constructor(top: string | null, thumb: string | null) {
    this.top = top;
    this.thumb = thumb;
  }
}
export class ServiceData {
  id: number;
  title: string | null;
  description: string | null;
  image: ServiceImageData | null;
  contentsUrl: string | null;
  externalContentsFlg: boolean;
  constructor(
    id: number,
    title: string | null,
    description: string | null,
    image: ServiceImageData | null,
    contentsUrl: string | null,
    externalContentsFlg: boolean
  ) {
    (this.id = id), (this.title = title);
    this.description = description;
    this.image = image;
    this.contentsUrl = contentsUrl;
    this.externalContentsFlg = externalContentsFlg;
  }
}

export class RequestRecommendationRecipe {
  foodList: Array<string> | null;
  isRefresh: boolean;
  constructor(foodList: Array<string> | null, isRefesh: boolean) {
    this.foodList = foodList;
    this.isRefresh = isRefesh;
  }
}

/**
 * API お気に入りフォルダ内のレシピ情報のタグ情報
 */
export interface FavoriteRecipeTag {
  id: number;
  name: string;
}
/**
 * API お気に入りフォルダ内のレシピ情報 レシピ定義
 */
 export class FavoriteRecipeClip {
  recipeId: number;
  caption: string;
  imageUrl: string;
  moduleType: string;
  device: string;
  applianceCategoryId: number;
  productId: string;
  groupId: number;
  type: string;
  tags: Array<FavoriteRecipeTag>;

  constructor() {
    this.recipeId = 0;
    this.caption = "";
    this.imageUrl = "";
    this.moduleType = "";
    this.device = "";
    this.applianceCategoryId = 0;
    this.productId = "";
    this.groupId = 0;
    this.type = "";
    this.tags = [];
  }
}

/**
 * お気に入りのレシピ取得結果
 */
export class FavoriteRecipeClips {
  clips: Array<FavoriteRecipeClip>;
  totalCount: number;
  start: number;
  lastUpdate: string;

  constructor() {
    this.clips = [];
    this.totalCount = 0;
    this.start = 0;
    this.lastUpdate = "";
  }
}

// 食品提案API
export interface FoodRecommendationReasonContentItem {
  type: string | null;
  id: number | null;
  image: string | null;
  url: string | null;
}
export class FoodRecommendationReasonItem {
  title: string;
  description: string;
  contents: Array<FoodRecommendationReasonContentItem> | null;
  constructor() {
    this.title = "";
    this.description = "";
    this.contents = null;
  }
}
export class FoodRecommendationListItem {
  foodId: number | null;
  pantryId: number | null;
  pantryPlateId: number | null;
  pantryFridgeId: number | null;
  name: string | null;
  image: string | null;
  icon: string | null;
  elapsedDays: number | null;
  inPantry: boolean;
  isFavorite: boolean;
  reason: FoodRecommendationReasonItem | null;
  constructor() {
    this.foodId = null;
    this.pantryId = null;
    this.pantryPlateId = null;
    this.pantryFridgeId = null;
    this.name = null;
    this.image = null;
    this.icon = null;
    this.elapsedDays = null;
    this.inPantry = false;
    this.isFavorite = false;
    this.reason = null;
  }
}

export class FoodRecommendationList {
  foods: Array<FoodRecommendationListItem>;
  recommendId: string;
  recommendTime: string;
  latestRecommended: string;
  reason: FoodRecommendationReasonItem | null;
  constructor() {
    this.foods = [];
    this.recommendId = "";
    this.recommendTime = "";
    this.latestRecommended = "";
    this.reason = null;
  }
}

export class FoodRecommendationListData {
  response: FoodRecommendationList | null;
  constructor(response: FoodRecommendationList | null) {
    this.response = response;
  }
}

export class FoodRecommendationListLine {
  foods: Array<FoodRecommendationListItem>;
  constructor(foods: Array<FoodRecommendationListItem>) {
    this.foods = foods;
  }
}

export class RequestFoodsRecommendationList {
  isRefresh: boolean;
  latestRecommended: string | undefined;
  constructor() {
    this.isRefresh = false;
    this.latestRecommended = undefined;
  }
}

export class RequestOneMenuRecommendation {
  date: string;
  refresh: boolean;
  foodList: Array<string>;
  constructor(date: string, refresh: boolean, foodList: Array<string>) {
    this.date = date;
    this.refresh = refresh;
    this.foodList = foodList;
  }
}

// 一食献立提案API
export interface OneMenuRecommendationNutritionItem {
  note: string,
  calories: number,
  salt: number;
  carbohydrate: number;
  lipid: number;
  protein: number;
}

export interface OneMenuRecommendationItem {
  nutrition: OneMenuRecommendationNutritionItem;
  servings: number;
  recipes: Array<Recipe>;
}

export interface OneMenuRecommendation {
  recommendDay: string;
  recommends: Array<OneMenuRecommendationItem>;
}

export class MenuDish {
  recipe: Recipe | null;
  foodIds: Array<CommonRecipeFoodId> | null
  constructor(recipe: Recipe | null, foodIds: Array<CommonRecipeFoodId> | null) {
    this.recipe = recipe;
    this.foodIds = foodIds;
  }
}

export class OneMenuRecommendationData {
  response: OneMenuRecommendation | null;
  dishs: Array<MenuDish> | null;
  constructor(response: OneMenuRecommendation | null,  dishs: Array<MenuDish> | null) {
    this.response = response;
    this.dishs = dishs;
  }
  getDish(position: number) {
    let recipe = null;
    if (this.dishs != null 
      && this.dishs.length >= 1) {
      if (this.dishs.length >= position + 1) {
        recipe = this.dishs[position];
      }
    }
    return recipe;
  }
}
